import { ButtonPrimary } from '@/components/button'
import { Close, UserIconFilled } from '@/components/icons'
import { BracketMatch } from '@/types'
import { Dialog, Transition } from '@headlessui/react'
import { Dispatch, Fragment, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  open: boolean
  onClose: () => void
  disabled: boolean
  match: BracketMatch
  onSubmit: () => void
  forfeitTeam: string | null
  setForfeitTeam: Dispatch<SetStateAction<string | null>>
}

export const ForfeitModal = ({ open, onClose, disabled, match, onSubmit, forfeitTeam, setForfeitTeam }: Props) => {
  const { t } = useTranslation()

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col gap-y-4 max-w-xl transform rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <button
                  className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-2 top-2"
                  onClick={onClose}
                >
                  <Close className="text-navy-900" />
                </button>

                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                  {t('event.forfeitMatch')}
                </Dialog.Title>

                <p className="text-sm text-navy-900 text-opacity-70">{t('event.forfeitMatchDesc')}</p>

                <div className="flex flex-col gap-y-6 mt-6">
                  <div className="flex flex-col gap-y-1">
                    <span className="text-sm text-navy-900">{t('event.teamA')}</span>
                    <label className="flex items-center gap-x-6 bg-gray-200 rounded py-4 pl-8 pr-20">
                      <input
                        value={t('event.teamA')}
                        checked={forfeitTeam === 'Team A'}
                        type="radio"
                        className="w-[18px] h-[18px]"
                        onChange={(e) => setForfeitTeam(e.target.value)}
                      />
                      <div className="flex flex-1">
                        <div className="flex w-3/5 items-center gap-x-2">
                          {match?.teams?.[0]?.player1?.imageUrl ? (
                            <img src={match?.teams?.[0]?.player1?.imageUrl} className="w-6 h-6 rounded-full" alt="" />
                          ) : (
                            <UserIconFilled className="w-6 h-6 text-primary-500" />
                          )}
                          <span className={`text-xs text-navy-900  font-medium`}>
                            {match?.teams?.[0]?.player1?.fullName}
                          </span>
                        </div>
                        <div className="flex w-2/5 items-center gap-x-2">
                          {match?.teams?.[0]?.player2?.imageUrl ? (
                            <img src={match?.teams?.[0]?.player2?.imageUrl} className="w-6 h-6 rounded-full" alt="" />
                          ) : (
                            <UserIconFilled className="w-6 h-6 text-primary-500" />
                          )}
                          <span className={`text-xs text-navy-900  font-medium`}>
                            {match?.teams?.[0]?.player2?.fullName}
                          </span>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div className="flex flex-col gap-y-1">
                    <span className="text-sm text-navy-900">{t('event.teamB')}</span>
                    <label className="flex items-center gap-x-6 bg-gray-200 rounded py-4 pl-8 pr-20">
                      <input
                        value={t('event.teamB')}
                        type="radio"
                        className="w-[18px] h-[18px]"
                        checked={forfeitTeam === 'Team B'}
                        onChange={(e) => setForfeitTeam(e.target.value)}
                      />
                      <div className="flex flex-1">
                        <div className="flex w-3/5 items-center gap-x-2">
                          {match?.teams?.[1]?.player1?.imageUrl ? (
                            <img src={match?.teams?.[1]?.player1?.imageUrl} className="w-6 h-6 rounded-full" alt="" />
                          ) : (
                            <UserIconFilled className="w-6 h-6 text-primary-500" />
                          )}
                          <span className={`text-xs text-navy-900  font-medium`}>
                            {match?.teams?.[1]?.player1?.fullName}
                          </span>
                        </div>
                        <div className="flex w-2/5 items-center gap-x-2">
                          {match?.teams?.[1]?.player2?.imageUrl ? (
                            <img src={match?.teams?.[1]?.player2?.imageUrl} className="w-6 h-6 rounded-full" alt="" />
                          ) : (
                            <UserIconFilled className="w-6 h-6 text-primary-500" />
                          )}
                          <span className={`text-xs text-navy-900  font-medium`}>
                            {match?.teams?.[1]?.player2?.fullName}
                          </span>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="flex justify-center gap-x-6">
                  <button
                    onClick={onClose}
                    className="bg-navy-900 hover:bg-opacity-20 bg-opacity-10 !py-[6px] !px-4 !rounded text-navy-900 font-semibold"
                  >
                    {t('common.cancel')}
                  </button>
                  <ButtonPrimary
                    title={t('event.markAsForfeit')}
                    className="!py-[6px] !px-4 !rounded"
                    disabled={disabled}
                    onClick={onSubmit}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
